import {App} from "@capacitor/app";
import {
    Capacitor,
    CapacitorException,
    ExceptionCode,
    type PluginListenerHandle,
    registerPlugin,
} from "@capacitor/core";
import {Device} from "@capacitor/device";
import {Directory, Filesystem} from "@capacitor/filesystem";
import {Haptics, ImpactStyle} from "@capacitor/haptics";
import {Preferences} from "@capacitor/preferences";
import {models, server} from "@reactivated";

export {Capacitor, CapacitorHttp, CapacitorCookies} from "@capacitor/core";
export {Preferences} from "@capacitor/preferences";
export {PushNotifications} from "@capacitor/push-notifications";

export {Filesystem, Encoding, Directory} from "@capacitor/filesystem";
export {App} from "@capacitor/app";
export type {URLOpenListenerEvent} from "@capacitor/app";

export {Network} from "@capacitor/network";
export {Keyboard, KeyboardResize} from "@capacitor/keyboard";

export {RateApp} from "capacitor-rate-app";
export {Haptics, ImpactStyle} from "@capacitor/haptics";

type HealthKitAggregate = {
    date: string;
    value: number;
    startDate: string;
    endDate: string;
};
export type HealthKitUnknown = {
    uuid: UUID;
    device: unknown;
    startDate: string;
    endDate: string;
    value: number;
    timezone?: string;
};

export type SleepState =
    | "Asleep"
    | "AsleepREM"
    | "AsleepCore"
    | "AsleepDeep"
    | "AsleepUnspecified"
    | "InBed"
    | "Awake";

export type HealthKitSleep = {
    uuid: UUID;
    device: unknown;
    sleepState: SleepState;
    startDate: string;
    endDate: string;
    duration: number;
    source: string;
    sourceBundleId: string;
    timezone?: string;
};

export type HealthKitWorkout = models["core.types.AppleHealthWorkoutData"];

type HealthKitSampleType =
    | "stepCount"
    | "sleepAnalysis"
    | "bodyMass"
    | "waistCircumference"
    | "activeEnergyBurned"
    | "bodyFatPercentage"
    | "basalEnergyBurned"
    | "workoutType"
    | "heartRate"
    | "workoutRoute"
    | "distanceWalkingRunning"
    | "distanceCycling"
    | "distanceSwimming";

interface HealthPlugin {
    querySample<
        TSample extends
            | "sleepAnalysis"
            | "bodyMass"
            | "waistCircumference"
            | "bodyFatPercentage"
            | "workoutType",
    >(queryOptions: {
        sample: TSample;
        limit: number;
        startDate: string;
        endDate: string;
    }): Promise<{
        countReturn: number;
        resultData: (TSample extends "sleepAnalysis"
            ? HealthKitSleep
            : TSample extends "workoutType"
              ? HealthKitWorkout
              : HealthKitUnknown)[];
    }>;
    queryAggregate(queryOptions: {
        sample: "stepCount" | "activeEnergyBurned" | "basalEnergyBurned";
        startDate: string;
        endDate: string;
        // Old binaries ignore this and answer daily — callers detect the
        // capability by the sub-day shape of the response, never by version.
        interval?: "hour";
    }): Promise<{results: HealthKitAggregate[]}>;
    // Earliest sample date (ISO string) across synced types, or null if none.
    // Only exists on native binaries new enough to ship it — call defensively.
    getEarliestSampleDate(): Promise<{earliestDate: string | null}>;
    isAvailable(): Promise<{isAvailable: boolean}>;
    requestAuthorization(authOptions: {
        read: HealthKitSampleType[];
        write: HealthKitSampleType[];
        all: HealthKitSampleType[];
    }): Promise<void>;
    getRequestStatusForAuthorization(authOptions: {
        read: HealthKitSampleType[];
        write: HealthKitSampleType[];
        all: HealthKitSampleType[];
    }): Promise<{status: "shouldRequest" | "unknown" | "unnecessary"}>;
    authorizationStatus(options: {
        sampleType: HealthKitSampleType;
    }): Promise<{status: "notDetermined" | "sharingDenied" | "sharingAuthorized"}>;
    // One sample per measurement and type under Joy's source, replaced in place
    // on edit. saved:false until the share authorization exists.
    saveMeasurement(
        options: HealthMeasurement,
    ): Promise<{saved: true; uuid: UUID} | {saved: false; reason: "unauthorized"}>;
    deleteMeasurement(options: {
        type: HealthMeasurementType;
        uuid: UUID;
    }): Promise<{deleted: boolean}>;
    // Any source's workout, with the heart rate over its window.
    getWorkout(options: {uuid: UUID}): Promise<{
        workout: HealthKitWorkout | null;
        heart_rate?: {min: number | null; avg: number | null; max: number | null};
    }>;
    getWorkoutRoute(options: {uuid: UUID}): Promise<{points: RoutePoint[]}>;
}

export type HealthMeasurementType =
    | "bodyMass"
    | "bodyFatPercentage"
    | "waistCircumference";

// In the units querySample reports: kilograms, a 0 to 1 fraction, centimeters.
export type HealthMeasurement = {
    type: HealthMeasurementType;
    uuid: UUID;
    value: DecimalValue;
    date: DateTimeValue;
};

export type RoutePoint = {
    at: DateTimeValue;
    latitude: number;
    longitude: number;
    altitude?: number;
    accuracy?: number;
};

export type DepthMap = {
    width: number;
    height: number;
    values: number[];
    orientation: number;
};

interface HotPlugin {
    finishedLaunching(): Promise<void>;
    requestTrackingAuthorization(): Promise<{status: number}>;
    reload(): Promise<void>;
    setVersion(options: {value: string}): Promise<void>;
    mixpanelIdentify(options: {distinct_id: string}): Promise<void>;
    mixpanelInit(options: {token: string}): Promise<void>;
    mixpanelReset(): Promise<void>;
    trackMeta(options: {event: string}): Promise<void>;
    showLiveActivity(options: {
        name: string;
        workoutURL: string;
    }): Promise<{id: string}>;
    updateLiveActivity(options: {
        id: string;
        restSeconds?: number | null;
    }): Promise<void>;
    endLiveActivity(options: {id: string}): Promise<void>;
    takePicture(options: {
        useDepthCamera: boolean;
        mode?: "meal" | "barcode" | "facts" | "barcode_only";
    }): Promise<
        | {
              status: "success";
              type: "meal";
              path: string;
              webPath: string;
              depth_map?: DepthMap;
          }
        | {
              status: "success";
              type: "barcode";
              barcode: string;
              path: string;
              webPath: string;
          }
        | {
              status: "success";
              type: "facts";
              path: string;
              webPath: string;
              depth_map?: DepthMap;
          }
        | {status: "canceled"}
        | {status: "no_permission"}
        | {status: "search"; type: "meal" | "barcode" | "facts"}
        | {status: "error"; error: string}
    >;
    openSettings(): Promise<void>;
}

export const HotPlugin = registerPlugin<HotPlugin>("HotPlugin");

export interface StoreProduct {
    id: string;
    displayName: string;
    displayPrice: string;
    isEligibleForIntroOffer: boolean;
    introOffer?: {displayPrice: string; isFreeTrial: boolean};
}

export interface StoreTransaction {
    id: string;
    productId: string;
    jws: string;
}

export interface PurchasesPlugin {
    getProducts(options: {productIds: string[]}): Promise<{products: StoreProduct[]}>;
    purchase(options: {
        productId: string;
        appAccountToken: string;
    }): Promise<
        | {status: "PURCHASED"; transaction: StoreTransaction}
        | {status: "CANCELLED"}
        | {status: "PENDING"}
    >;
    getUnfinishedTransactions(): Promise<{transactions: StoreTransaction[]}>;
    getCurrentEntitlements(): Promise<{transactions: StoreTransaction[]}>;
    finishTransaction(options: {id: string}): Promise<void>;
    restore(): Promise<{transactions: StoreTransaction[]}>;
    manageSubscriptions(): Promise<void>;
    addListener(
        eventName: "transactionUpdated",
        listener: (transaction: StoreTransaction) => void,
    ): Promise<PluginListenerHandle>;
}

export const PurchasesPlugin = registerPlugin<PurchasesPlugin>("PurchasesPlugin");

// False on binaries below 12.76.0, which buy through cordova-plugin-purchase.
export const hasPurchasesPlugin = () => Capacitor.isPluginAvailable("PurchasesPlugin");

export const LIVE_ACTIVITY_ID_KEY = "liveActivity";

// Memoize a promise factory: caches the promise (so React.use() gets a stable
// reference across renders) but drops it on rejection, leaving failures retryable.
// Args are forwarded to the first call and ignored afterwards, so this is only for
// idempotent reads where the result is stable for the session.
const memoizePromise = <TArgs extends unknown[], T>(
    factory: (...args: TArgs) => Promise<T>,
): ((...args: TArgs) => Promise<T>) => {
    let cached: Promise<T> | null = null;
    return (...args: TArgs) => {
        if (cached == null) {
            cached = factory(...args);
            cached.catch(() => {
                cached = null;
            });
        }
        return cached;
    };
};

const _getAppInfo = async () => {
    const build = parseInt(
        process.env.BUILD_VERSION ?? Number.MAX_SAFE_INTEGER.toString(),
    );
    // Artificially high default to prevent local builds from auto updating.
    const version = process.env.RELEASE_VERSION ?? "v1000.1000.1000";
    // const version = "donotupdate"

    const _getAppInfo = async () => {
        if (window.cordova != null) {
            const appInfo = await App.getInfo();

            return {
                appVersion: `v${appInfo.version}`,
                applicationId: appInfo.id,
                platform: Capacitor.getPlatform() as "android" | "ios",
            };
        }
        return {
            appVersion: version,
            applicationId: window.location.hostname.split(".").reverse().join("."),
            platform: "web" as const,
        };
    };

    const {appVersion, platform, applicationId} = await _getAppInfo();
    const [major, minor, patch] = appVersion.slice(1).split(".");

    return {
        version,
        deviceUUID: (await Device.getId()).uuid,
        build,
        platform,
        applicationId,
        appVersion,
        app: {
            major: parseInt(major),
            minor: parseInt(minor),
            patch: parseInt(patch),
        },
    };
};

export const getAppInfo = memoizePromise(_getAppInfo);

export const nativeOnly = async (callback: () => Promise<void> | void) => {
    const {platform} = await getAppInfo();

    // In case we haven't implemented this yet.
    if (platform !== "ios" && platform !== "android") {
        return;
    }

    try {
        await callback();
    } catch (exception: unknown) {
        if (
            exception instanceof CapacitorException &&
            exception.code == ExceptionCode.Unimplemented
        ) {
            // If the bundle is newer than the native app.
        } else {
            throw exception;
        }
    }
};

async function checkReleaseVersion() {
    const {platform, app} = await getAppInfo();

    if (platform != "ios") {
        return false;
    }

    if (app.major == 0 && app.minor == 0 && app.patch == 0) {
        return true;
    }

    const currentVersion = [app.major, app.minor, app.patch];
    const minimumVersion = [12, 24, 0];

    // Compare versions
    for (let i = 0; i < minimumVersion.length; i++) {
        if (currentVersion[i] < minimumVersion[i]) {
            return false;
        }
        if (currentVersion[i] > minimumVersion[i]) {
            return true;
        }
    }

    return true;
}

/**
 * Returns the URL scheme for deep links based on the app's bundle ID.
 * Production (com.joyapp.ios) uses the official "com.joyapp" scheme.
 * Dev environments use their bundle ID directly (e.g., com.joyapp.ios.localhost.regression).
 */
async function getDeepLinkScheme(): Promise<string> {
    const info = await App.getInfo();
    const bundleId = info.id;

    if (bundleId === "com.joyapp.ios") {
        return "com.joyapp";
    }
    return bundleId;
}

export async function startLiveActivity(options: {
    workout:
        | server.journal.workouts.Workout.output
        | server.journal.workouts.IncompleteWorkout.output;
}) {
    if ((await checkReleaseVersion()) == false) {
        return;
    }
    try {
        const router = await import("@client/router");
        const workoutURLLocator = router.locator({
            route: "journal.workout",
            params: {key: options.workout.date!},
        });

        const scheme = await getDeepLinkScheme();
        const activity = await HotPlugin.showLiveActivity({
            name: options.workout.name ?? "Custom Workout",
            workoutURL: `${scheme}://redirect${workoutURLLocator.url}`,
        });
        localStorage.setItem(LIVE_ACTIVITY_ID_KEY, activity.id);
    } catch (error) {
        console.error("Failed to start Live Activity via HotPlugin:", error);
        localStorage.removeItem(LIVE_ACTIVITY_ID_KEY);
    }
}

export async function updateLiveActivity(options: {restSeconds?: number | null}) {
    if ((await checkReleaseVersion()) == false) {
        return;
    }
    // No stored id is fine: a recording session may have started the activity.
    const id = localStorage.getItem(LIVE_ACTIVITY_ID_KEY) ?? "";
    try {
        await HotPlugin.updateLiveActivity({
            id,
            restSeconds: options.restSeconds,
        });
    } catch (error) {
        console.error("Failed to update Live Activity via HotPlugin:", error);
    }
}

export async function endLiveActivity() {
    if ((await checkReleaseVersion()) == false) {
        return;
    }
    const id = localStorage.getItem(LIVE_ACTIVITY_ID_KEY) ?? "";
    try {
        await HotPlugin.endLiveActivity({id});
    } catch (error) {
        console.error("Failed to end Live Activity via HotPlugin:", error);
    } finally {
        localStorage.removeItem(LIVE_ACTIVITY_ID_KEY);
    }
}

export const HealthPlugin = registerPlugin<HealthPlugin>("HealthPlugin");

// A recorded workout session. Swift owns the clock and the whole truth: JS never
// runs a timer, it renders the last event and re-reads status() on resume.
export type WorkoutSessionState =
    | "unsupported"
    | "idle"
    | "running"
    | "paused"
    | "completed";

export type WorkoutEnvironment = "INDOOR" | "OUTDOOR" | "ANY";

// The set's distance in the set's unit; the plugin saves it in that unit and
// HealthKit converts on read.
export type DistanceUnit = NonNullable<
    server.journal.workouts.ExerciseSet.output["distance_type"]
>;

export type WorkoutDistance = {
    apple_health_uuid: UUID;
    exercise_uuid: UUID;
    distance: DecimalValue;
    unit: DistanceUnit;
};

// A finished session, held by the plugin until JS has saved it and called
// acknowledge(). Same UUIDs on every replay, so the save is idempotent.
export type WorkoutResult = {
    exercise_uuid: UUID;
    apple_health_uuid: UUID;
    started_at: DateTimeValue;
    ended_at: DateTimeValue;
    duration_seconds: SecondsValue;
    distance_meters: number | null;
    // The HealthKit workout carries no distance of its own; older binaries omit it.
    distance_detached?: boolean;
    energy_kcal: number | null;
    heart_rate_min: number | null;
    heart_rate_avg: number | null;
    heart_rate_max: number | null;
};

export type WorkoutImport = {
    exercise_uuid: UUID;
    activity: string;
    environment: WorkoutEnvironment;
    started_at: DateTimeValue;
    ended_at: DateTimeValue;
    timezone?: string;
    distance_meters?: number;
    energy_kcal?: number;
    heart_rate?: {at: DateTimeValue; bpm: number}[];
    route?: RoutePoint[];
};

export type WorkoutStatus = {
    state: WorkoutSessionState;
    exercise_uuid?: UUID;
    started_at?: DateTimeValue;
    elapsed_seconds?: SecondsValue;
    heart_rate?: number | null;
    heart_rate_at?: DateTimeValue | null;
    distance_meters?: number | null;
    energy_kcal?: number | null;
    result?: WorkoutResult;
};

export type WorkoutMetricsEvent = {
    elapsed_seconds: SecondsValue;
    heart_rate: number | null;
    heart_rate_at: DateTimeValue | null;
    distance_meters: number | null;
    energy_kcal: number | null;
};

export type WorkoutStateEvent = {state: WorkoutSessionState};

interface WorkoutPlugin {
    status(): Promise<WorkoutStatus>;
    start(options: {
        exercise_uuid: UUID;
        activity: string;
        environment: WorkoutEnvironment;
        weight_kg: DecimalValue | null;
    }): Promise<void>;
    pause(): Promise<void>;
    resume(): Promise<void>;
    end(): Promise<void>;
    acknowledge(): Promise<void>;
    discard(options: {apple_health_uuid: UUID}): Promise<void>;
    // A manual lap: a marker event on the HealthKit workout, nothing in Joy.
    mark(): Promise<void>;
    setDistance(options: WorkoutDistance): Promise<{saved: boolean}>;
    // A workout done elsewhere, written under Joy's source; answers like End.
    importWorkout(options: WorkoutImport): Promise<WorkoutResult>;
    addListener(
        eventName: "workoutMetrics",
        listener: (event: WorkoutMetricsEvent) => void,
    ): Promise<PluginListenerHandle>;
    addListener(
        eventName: "workoutState",
        listener: (event: WorkoutStateEvent) => void,
    ): Promise<PluginListenerHandle>;
}

export const WorkoutPlugin = registerPlugin<WorkoutPlugin>("WorkoutPlugin");

type NativeMethodResult<T> = {status: "success"; data: T} | {status: "not_implemented"};

export const nativeMethod = <TArgs extends unknown[], TReturn>(
    method: (...args: TArgs) => Promise<TReturn>,
) => {
    return async (...args: TArgs): Promise<NativeMethodResult<TReturn>> => {
        const {platform} = await getAppInfo();

        if (platform !== "ios" && platform !== "android") {
            return {status: "not_implemented"};
        }

        try {
            const data = await method(...args);
            return {status: "success", data};
        } catch (exception: unknown) {
            if (
                exception instanceof CapacitorException &&
                exception.code === ExceptionCode.Unimplemented
            ) {
                return {status: "not_implemented"};
            }
            throw exception;
        }
    };
};

export const takePicture = nativeMethod(
    (options: {
        useDepthCamera: boolean;
        mode?: "meal" | "barcode" | "facts" | "barcode_only";
    }) => HotPlugin.takePicture(options),
);
export const openSettings = nativeMethod(() => HotPlugin.openSettings());

export const getWorkoutStatus = nativeMethod(() => WorkoutPlugin.status());
export const startWorkout = nativeMethod(
    (options: {
        exercise_uuid: UUID;
        activity: string;
        environment: WorkoutEnvironment;
        weight_kg: DecimalValue | null;
    }) => WorkoutPlugin.start(options),
);
export const pauseWorkout = nativeMethod(() => WorkoutPlugin.pause());
export const resumeWorkout = nativeMethod(() => WorkoutPlugin.resume());
export const endWorkout = nativeMethod(() => WorkoutPlugin.end());
export const acknowledgeWorkout = nativeMethod(() => WorkoutPlugin.acknowledge());
export const discardWorkout = (apple_health_uuid: UUID) =>
    nativeOnly(() => WorkoutPlugin.discard({apple_health_uuid}));
export const importWorkout = nativeMethod((options: WorkoutImport) =>
    WorkoutPlugin.importWorkout(options),
);
export const markWorkout = nativeMethod(() => WorkoutPlugin.mark());
export const setWorkoutDistance = nativeMethod((options: WorkoutDistance) =>
    WorkoutPlugin.setDistance(options),
);

export const addWorkoutMetricsListener = nativeMethod(
    (listener: (event: WorkoutMetricsEvent) => void) =>
        WorkoutPlugin.addListener("workoutMetrics", listener),
);
export const addWorkoutStateListener = nativeMethod(
    (listener: (event: WorkoutStateEvent) => void) =>
        WorkoutPlugin.addListener("workoutState", listener),
);
// memoizePromise so AppleHealthResyncCard can read this via React.use(), which
// needs a stable promise across renders. Safe to cache: the earliest sample date
// is effectively immutable for a session.
// The rest of PurchasesPlugin runs only once getStoreProducts has succeeded.
export const getStoreProducts = nativeMethod((productIds: string[]) =>
    PurchasesPlugin.getProducts({productIds}),
);
export const purchaseProduct = nativeMethod(
    (options: {productId: string; appAccountToken: string}) =>
        PurchasesPlugin.purchase(options),
);
export const manageSubscriptions = () =>
    nativeOnly(() => PurchasesPlugin.manageSubscriptions());

export const saveMeasurement = nativeMethod((options: HealthMeasurement) =>
    HealthPlugin.saveMeasurement(options),
);
export const deleteMeasurement = nativeMethod(
    (options: {type: HealthMeasurementType; uuid: UUID}) =>
        HealthPlugin.deleteMeasurement(options),
);
export const getHealthWorkout = nativeMethod((uuid: UUID) =>
    HealthPlugin.getWorkout({uuid}),
);
export const getHealthWorkoutRoute = nativeMethod((uuid: UUID) =>
    HealthPlugin.getWorkoutRoute({uuid}),
);
export const getEarliestSampleDate = memoizePromise(
    nativeMethod(() => HealthPlugin.getEarliestSampleDate()),
);

export const HEALTHKIT_PERMISSIONS = {
    all: [
        "bodyMass",
        "waistCircumference",
        "bodyFatPercentage",
        "workoutType",
    ] as HealthKitSampleType[],
    read: [
        "stepCount",
        "sleepAnalysis",
        "basalEnergyBurned",
        "activeEnergyBurned",
    ] as HealthKitSampleType[],
    write: [] as HealthKitSampleType[],
};

export function haptic(style: keyof typeof ImpactStyle = "Medium") {
    void Haptics.impact({
        style: ImpactStyle[style],
    });
}

const compress = async (input: string): Promise<string> => {
    const stream = new Blob([input])
        .stream()
        .pipeThrough(new CompressionStream("deflate-raw"));
    const buffer = new Uint8Array(await new Response(stream).arrayBuffer());
    return Array.from(buffer, (b) => String.fromCharCode(b)).join("");
};

const decompress = async (input: string): Promise<string> => {
    const bytes = Uint8Array.from(input, (c) => c.charCodeAt(0));
    const stream = new Blob([bytes])
        .stream()
        .pipeThrough(new DecompressionStream("deflate-raw"));
    return new Response(stream).text();
};

export const getCache = async <T,>(
    key: string,
    cacheVersion?: string,
): Promise<T | null> => {
    const version = cacheVersion ?? (await getAppInfo()).version;
    const rawCache = localStorage.getItem(key);
    if (rawCache != null) {
        try {
            const json = await decompress(rawCache);
            const cache = JSON.parse(json) as {
                version: string;
                data: T;
            };

            if (cache.version == version) {
                return cache.data;
            }
        } catch {
            return null;
        }
    }
    return null;
};

export const setCache = async (key: string, data: unknown, cacheVersion?: string) => {
    const version = cacheVersion ?? (await getAppInfo()).version;
    const compressed = await compress(JSON.stringify({version, data}));
    localStorage.setItem(key, compressed);
};

export const getBackgroundNotification = async () => {
    const key = "JOY_BACKGROUND_NOTIFICATION";
    const {value} = await Preferences.get({key});
    await Preferences.remove({key});

    if (value != null) {
        try {
            return JSON.parse(value) as object;
        } catch {
            return null;
        }
    }
    return null;
};

export async function dataDirectory() {
    const {platform} = await getAppInfo();

    // If this changes, see boot.tsx as well.
    const prefix = platform == "ios" ? "NoCloud/ionic_built_snapshots/" : "";

    const existingVersions = await Filesystem.readdir({
        path: prefix,
        directory: Directory.Library,
    });

    return {
        existingVersions,
    };
}

export const getTag = async () => {
    if (window.__E2E_JOY_TAG__ != null) {
        return window.__E2E_JOY_TAG__;
    }
    if (Capacitor.getPlatform() == "ios") {
        const {value} = await Preferences.get({key: "JOY_TAG"});
        return value;
    }
    return null;
};

declare global {
    interface Window {
        cordova?: unknown;
    }
}
